import React from 'react';
import clsx from 'clsx';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { getClientTimezone, stringToDate } from '@dxb-web-ui-framework/common/utils/Date';
import { Box, Typography, makeStyles, Checkbox, Button, Tooltip } from '@dxb-web-ui-framework/design-system';
import { Info as InfoIcon } from '@dxb-web-ui-framework/design-system/dist/icons';
// import InfoIcon from '@material-ui/icons/Info';
import { useTranslation } from 'react-i18next';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import { Visibility as VisibilityIcon } from '@dxb-web-ui-framework/design-system/dist/icons';
import {
  actionAuthorizationButtonsRenderer,
  formatAmountCellRenderer,
  stringToDateCellRenderer,
} from '../../../content-composer/src/components/Generic/TableGrid/tableGridCellRenderers/tableGridCellRenderers';
import { formatAmountTransformer } from '../../../content-composer/src/components/Generic/TableGrid/tableGridMobileListTransformers/tableGridMobileListTransformers';
import { TableGridConfig } from '../../../content-composer/src/components/Generic/types';
import { Mandate } from '../domain/Authorization/entities';
import { useOnBulkAuthorizationSelect } from '../contexts';
import { useBulkCheckboxLogicHandler } from '../hooks';
import { ProcessStatuses } from './ApproveRejectAuthFlowPresenter';
import { BulkAuthorizationCheckbox } from './BulkAuthorizationCheckbox';

export enum OrderProcessTypes {
  SEPA = 'SEPA transfer',
  SWIFT = 'SWIFT transfer',
  INTTR = 'Internal transfer',
}

const useStyles = makeStyles(theme => ({
  actionsAuthorizationsContainer: {
    [theme.breakpoints.down('sm')]: {
      position: 'absolute',
      right: theme.spacing(3),
      bottom: theme.spacing(2),
      left: theme.spacing(1),
    },
  },
  boldText: {
    color: theme.palette.secondary.main,
  },
  text: {
    fontSize: '14px',
  },
  authActionText: {
    padding: `${theme.spacing(0.5)}px ${theme.spacing(2)}px`,
    color: 'white',
    '&:hover': {
      cursor: 'pointer',
      opacity: '0.8',
    },
  },
  reject: {
    backgroundColor: theme.palette.primary.main,
    padding: `${theme.spacing(0.5)}px ${theme.spacing(2.8)}px`,
  },
  approve: {
    backgroundColor: theme.palette.success.main,
  },
  disabledAuthActionText: {
    backgroundColor: `${theme.palette.text.disabled} !important`,
    '&:hover': {
      cursor: 'default',
      opacity: '1',
    },
  },
  bulkSelectText: {
    marginRight: theme.spacing(2),
  },
  accountContainer: {
    paddingBottom: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      paddingBottom: theme.spacing(8),
    },
  },
  disabledText: {
    color: theme.palette.text.disabled,
  },
  rowCheckBoxContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  beneficiaryContainer: {
    paddingBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      minHeight: theme.spacing(15),
    },
    [theme.breakpoints.down('xs')]: {
      minHeight: theme.spacing(17),
    },
  },
}));

const useAuthorizationGridConfig = (
  actionCallback: Function,
  bulkSelectedAuthorizations: Mandate[],
  onSelectListItem: (rowData: Mandate) => void,
  authList: Nullable<Mandate[]>,
  onBulkMandatesAction?: (name: string) => void,
): TableGridConfig => {
  const { t } = useTranslation();
  const locale = t('global.settings.date.locale');
  const dateFormat = t('global.settings.date.format');
  const timezone = getClientTimezone();
  const onSelectAuthorization = useOnBulkAuthorizationSelect();
  const { isIntermediate, isChecked, isLoadingAuthorizationList, onChange, isMobile } = useBulkCheckboxLogicHandler();

  const isNotBulkAuthorizationSelected = isEmpty(bulkSelectedAuthorizations);

  const isCheckBoxSelected = (transactionId: string): boolean => {
    if (transactionId && !isNotBulkAuthorizationSelected) {
      const isAuthFound = bulkSelectedAuthorizations?.find(ba => ba?.transactionId === transactionId);
      return Boolean(isAuthFound);
    }
    return false;
  };

  const classes = useStyles();

  return {
    gridOptions: {
      ariaTitle: t('authorizationListPage.useAuthorizationGridConfig.gridOptions.ariaTitle'),
      dataTestId: 'Recent-authorizations-table',
      noResultsMsg: t('authorizationListPage.useAuthorizationGridConfig.gridOptions.noResultsMsg'),
      padding: 'normal',
      size: 'medium',
      stickyHeader: false,
      collapseToList: true,
    },
    isRowSelected: rowData => isCheckBoxSelected(rowData.transactionId),
    columnDefs: [
      {
        id: 'BulkAction',
        field: '',
        label: t('authorizationListPage.useAuthorizationGridConfig.columnDefs.Check'),
        cellRenderer: rowData => (
          <Box className={classes.rowCheckBoxContainer}>
            <Checkbox
              key={rowData.transactionId}
              id={rowData.transactionId}
              size='small'
              disabled={rowData.processStatus === ProcessStatuses.PENDING}
              checked={isCheckBoxSelected(rowData.transactionId)}
              onChange={() => onSelectAuthorization(rowData.transactionId as string)}
            />
            {rowData.processStatus !== ProcessStatuses.PENDING && rowData.eligibleForDealerRate && (
              <Tooltip title={t('authorizationListPage.useAuthorizationGridConfig.columnDefs.Tooltip')}>
                <InfoIcon color='primary' />
              </Tooltip>
            )}
          </Box>
        ),
        columnHeaderRenderer: () => (
          <BulkAuthorizationCheckbox
            isIntermediate={isIntermediate}
            isChecked={isChecked}
            isLoadingAuthorizationList={isLoadingAuthorizationList}
            onChange={onChange}
            isMobile={isMobile}
          />
        ),
      },
      {
        id: 'TransactionId',
        field: 'globalOrderId',
        label: t('authorizationListPage.useAuthorizationGridConfig.columnDefs.TransactionId'),
      },
      {
        id: 'Beneficiary',
        field: 'beneficiaryName',
        label: t('authorizationListPage.useAuthorizationGridConfig.columnDefs.Beneficiary'),
      },
      {
        id: 'Account',
        field: 'account',
        label: t('authorizationListPage.useAuthorizationGridConfig.columnDefs.Account'),
      },
      {
        id: 'Username',
        field: 'username',
        label: t('authorizationListPage.useAuthorizationGridConfig.columnDefs.Username'),
      },
      {
        id: 'PostingDate',
        field: 'postingDate',
        label: t('authorizationListPage.useAuthorizationGridConfig.columnDefs.PostingDate'),
        cellRenderer: stringToDateCellRenderer(dateFormat, locale, timezone),
      },
      {
        id: 'Amount',
        field: 'amount',
        label: t('authorizationListPage.useAuthorizationGridConfig.columnDefs.Amount'),
        cellRenderer: formatAmountCellRenderer(locale, false, 'currency'),
        columnHeaderCellProps: { align: 'right' },
        cellProps: { align: 'right' },
      },
      {
        id: 'Actions',
        field: 'actions',
        label: t('authorizationListPage.useAuthorizationGridConfig.columnDefs.Actions'),
        columnHeaderCellProps: { align: 'center' },
        cellRenderer: actionAuthorizationButtonsRenderer(actionCallback),
      },
      {
        id: 'viewAction',
        field: '',
        label: '',
        columnHeaderCellProps: { align: 'center' },
        cellRenderer: rowData => (
          <Button
            key={rowData.transactionId}
            fullWidth
            variant='text'
            size='small'
            color='secondary'
            isIconButton={true}
            disabled={rowData.processStatus === ProcessStatuses.PENDING}
            onClick={e => actionCallback(e, 'viewDetails', rowData.transactionId, false)}>
            <VisibilityIcon color='inherit' />
          </Button>
        ),
      },
    ],
    mobileListConfig: {
      listItemContents: [
        {
          label: 'orderProcessType',
          value: 'beneficiaryName',
          valueAreaLabel: 'beneficiaryName',
          labelColor: 'textPrimary',
          labelSize: 'large',
          valueSize: 'small',
          valueColor: 'secondary',
          labelTransformer: data => OrderProcessTypes[data.orderProcessType as keyof typeof OrderProcessTypes],
          valueTransformer: (data: Mandate) => (
            <Box mt={1} key={data.transactionId} className={classes.beneficiaryContainer}>
              <Typography gutterBottom color='textSecondary' className={classes.text}>
                {data.beneficiaryName}
              </Typography>
              <Typography color='textSecondary' className={classes.text}>
                {`${stringToDate({
                  date: data.postingDate,
                  formatExp: dateFormat,
                  localeName: locale,
                  tz: timezone,
                })}, from `}
                <b className={classes.boldText}>{data.username}</b>
              </Typography>
            </Box>
          ),
        },
        {
          label: 'amount',
          value: 'account',
          valueAreaLabel: 'approve',
          labelColor: 'textPrimary',
          labelSize: 'large',
          valueSize: 'small',
          valueColor: 'secondary',
          labelAlignment: 'right',
          valueAlignment: 'right',
          labelTransformer: formatAmountTransformer('amount', 'currency', 2, locale),
          valueTransformer: (data: Mandate) => (
            <Box pt={1} className={classes.accountContainer} key={data.transactionId}>
              <Typography color='textSecondary' className={classes.text}>
                {data.account}
              </Typography>
            </Box>
          ),
        },
      ],
      listItemOnSelect: onSelectListItem,
      listItemActions: (rowData: any, columnDef: any) => (
        <Box key={rowData.transactionId} className={classes.actionsAuthorizationsContainer}>
          {actionAuthorizationButtonsRenderer(
            actionCallback,
            <Box className={classes.rowCheckBoxContainer}>
              <Checkbox
                size='small'
                id={rowData.transactionId}
                checked={isCheckBoxSelected(rowData.transactionId)}
                disabled={rowData.processStatus === ProcessStatuses.PENDING}
                onChange={() => onSelectAuthorization(rowData.transactionId as string)}
              />
              {rowData.processStatus !== ProcessStatuses.PENDING && rowData.eligibleForDealerRate && (
                <Typography variant='subtitle2' color='primary'>
                  {t('authorizationListPage.useAuthorizationGridConfig.columnDefs.Tooltip')}
                </Typography>
              )}
            </Box>,
          )(rowData, columnDef)}
        </Box>
      ),
    },
    actions: [
      {
        dataTestId: 'authorization-reject',
        variant: 'text',
        color: 'secondary',
        size: 'small',
        disabled: false,
        onClick: () => {},
        isIconButton: true,
        Component: ({ keyIndex }: { keyIndex: number }) => (
          <Box key={keyIndex}>
            <Typography
              onClick={() => onBulkMandatesAction && !isNotBulkAuthorizationSelected && onBulkMandatesAction('reject')}
              paragraph
              component='span'
              variant='body2'
              className={clsx(classes.authActionText, classes.reject, {
                [classes.disabledAuthActionText]: isNotBulkAuthorizationSelected,
              })}>
              {t(
                bulkSelectedAuthorizations?.length === authList?.length
                  ? 'authorizationListPage.ActionAuthorizationButtons.RejectAll'
                  : 'authorizationListPage.ActionAuthorizationButtons.RejectSelected',
              )}
            </Typography>
          </Box>
        ),
        title: t(
          bulkSelectedAuthorizations?.length === authList?.length
            ? 'authorizationListPage.ActionAuthorizationButtons.RejectAll'
            : 'authorizationListPage.ActionAuthorizationButtons.RejectSelected',
        ),
      },
      {
        dataTestId: 'authorization-approve',
        variant: 'text',
        color: 'secondary',
        size: 'large',
        disabled: false,
        onClick: () => {},
        Component: ({ keyIndex }: { keyIndex: number }) => (
          <Box ml={2} key={keyIndex}>
            <Typography
              onClick={() => onBulkMandatesAction && !isNotBulkAuthorizationSelected && onBulkMandatesAction('approve')}
              paragraph
              component='span'
              variant='body2'
              className={clsx(classes.authActionText, classes.approve, {
                [classes.disabledAuthActionText]: isNotBulkAuthorizationSelected,
              })}>
              {t(
                bulkSelectedAuthorizations?.length === authList?.length
                  ? 'authorizationListPage.ActionAuthorizationButtons.ApproveAll'
                  : 'authorizationListPage.ActionAuthorizationButtons.ApproveSelected',
              )}
            </Typography>
          </Box>
        ),
        isIconButton: true,
        title: t(
          bulkSelectedAuthorizations?.length === authList?.length
            ? 'authorizationListPage.ActionAuthorizationButtons.ApproveAll'
            : 'authorizationListPage.ActionAuthorizationButtons.ApproveSelected',
        ),
      },
      {
        dataTestId: 'authorization-count',
        variant: 'text',
        color: 'secondary',
        size: 'large',
        disabled: false,
        onClick: () => {},
        Component: ({ keyIndex }: { keyIndex: number }) => (
          <Box ml={2} key={keyIndex}>
            {bulkSelectedAuthorizations?.length > 0 && (
              <Typography paragraph component='span' variant='subtitle2'>
                {t('authorizationListPage.ActionAuthorizationButtons.selectedItems', {
                  selectedItems: bulkSelectedAuthorizations?.length,
                })}
              </Typography>
            )}
          </Box>
        ),
        isIconButton: false,
        title: t('authorizationListPage.ActionAuthorizationButtons.selectedItems', {
          selectedItems: bulkSelectedAuthorizations?.length,
        }),
      },
    ],
  };
};

export { useAuthorizationGridConfig };
